import {
  Box,
  FormControl,
  IconButton,
  InputLabel,
  ListItem,
  MenuItem,
  Select,
  TextField,
  Typography,
  Tooltip,
} from '@mui/material';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';

import React, { useMemo } from 'react';
import { FormikErrors, FormikTouched } from 'formik';
import { DatePicker } from '@mui/x-date-pickers';
import { dateToIsoString, formatStringToDate } from '../../../utils';

import _ from 'lodash';
import { COLORS } from '../../../utils/colors';
import { HeadcountChange, ProjectDetail } from '../../../types';
import { HeadcountChangeFormik } from '../EditHeadcountChangesModal';

// Custom FormErrorHelperText
const FormErrorHelperText: React.FC<{ error?: string }> = ({ error }) => {
  // Two line height, will not jump on error/no-error
  const minHeight = 48; // px, two lines

  return (
    <Box
      sx={{
        minHeight,
        height: minHeight,
        width: '100%',
        margin: 0,
        position: 'relative',
        display: 'flex',
        alignItems: 'flex-start', // Align the text at the bottom
      }}
    >
      {error ? (
        <Tooltip
          title={error}
          open={false}
          arrow
          placement="top"
          disableHoverListener={false}
          disableFocusListener={true}
          disableTouchListener={true}
        >
          <Typography
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'normal', // Allow line wrap for up to 2 lines
              color: 'error.main',
              fontSize: '0.75rem',
              width: '100%',
              lineHeight: '1.25', // Default line height for better readability
              maxHeight: minHeight,
              margin: 0,
              wordBreak: 'break-word',
            }}
            component="div"
          >
            {error}
          </Typography>
        </Tooltip>
      ) : (
        // When no error, render empty space to preserve height
        <Box sx={{ height: minHeight, width: '100%' }} />
      )}
    </Box>
  );
};

interface HeadcountChangeRowProps {
  hcGroupKey: string;
  headcountChanges: HeadcountChange[];
  projects: ProjectDetail[] | undefined;
  isSingleProjectSelected: boolean;
  handleDataChange: (
    field: string,
    value: number | string | null,
    index: number,
  ) => void;
  handleDeleteHeadcount: (indexToBeDeleted: number, id?: string) => void;
  findIndex: (id: string) => number;
  errors: FormikErrors<HeadcountChangeFormik>;
  touched: FormikTouched<HeadcountChangeFormik>;
}

const HeadcountChangeGroup: React.FC<HeadcountChangeRowProps> = ({
  findIndex,
  hcGroupKey,
  headcountChanges,
  projects,
  handleDataChange,
  handleDeleteHeadcount,
  isSingleProjectSelected,
  errors,
  touched,
}) => {
  const projectsMap = useMemo(
    () => new Map(projects?.map((project) => [project.id, project.name])),
    [projects],
  );

  return (
    <>
      {!isSingleProjectSelected && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            pl: 0,
            backgroundColor: COLORS.HIGHLIGHTS.NEUTRAL,
            padding: 2,
            marginBottom: 2,
            borderRadius: 2,
          }}
        >
          <ListItem sx={{ paddingY: 0, paddingX: 0 }}>
            <Typography whiteSpace="nowrap" variant="subtitle1">
              {hcGroupKey === 'new'
                ? 'New projected headcounts'
                : projectsMap.get(hcGroupKey)}
            </Typography>
          </ListItem>
        </Box>
      )}

      {headcountChanges.map((headcountChange) => (
        <HeadcountChangeRow
          isSingleProjectSelected={isSingleProjectSelected}
          handleDataChange={handleDataChange}
          handleDeleteHeadcount={handleDeleteHeadcount}
          findIndex={findIndex}
          key={headcountChange.id ?? headcountChange.tempId}
          headcountChange={headcountChange}
          projects={projects}
          errors={errors}
          touched={touched}
        />
      ))}
    </>
  );
};

const HeadcountChangeRow: React.FC<{
  isSingleProjectSelected: boolean;
  handleDataChange: (
    field: string,
    value: number | string | null,
    index: number,
  ) => void;
  handleDeleteHeadcount: (indexToBeDeleted: number, id?: string) => void;
  findIndex: (id: string) => number;
  headcountChange: HeadcountChange;
  projects: ProjectDetail[] | undefined;
  errors: FormikErrors<HeadcountChangeFormik>;
  touched: FormikTouched<HeadcountChangeFormik>;
}> = ({
  isSingleProjectSelected,
  handleDataChange,
  handleDeleteHeadcount,
  findIndex,
  headcountChange,
  projects,
  errors,
  touched,
}) => {
  const headcountChangeIndex = useMemo(() => {
    return findIndex(headcountChange.id ?? headcountChange.tempId ?? '');
  }, [headcountChange.id, headcountChange.tempId, findIndex]);

  const errorObj = useMemo(() => {
    return errors.headcountChanges?.[headcountChangeIndex] as
      | FormikErrors<HeadcountChange>
      | undefined;
  }, [errors, headcountChangeIndex]);

  const touchedObj = useMemo(() => {
    return touched.headcountChanges?.[headcountChangeIndex] as
      | FormikTouched<HeadcountChange>
      | undefined;
  }, [touched, headcountChangeIndex]);

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          marginBottom: 2,
          px: isSingleProjectSelected ? 0 : 2,
        }}
        key={headcountChange.id ?? headcountChange.tempId}
      >
        <FormControl sx={{ width: '50%' }}>
          <DatePicker
            onChange={(date) =>
              handleDataChange(
                'date',
                dateToIsoString(date),
                headcountChangeIndex,
              )
            }
            disabled={headcountChange.delete}
            value={formatStringToDate(headcountChange.date)}
            format="MM/dd/yyyy"
            label="Date"
            disablePast
            disableHighlightToday
            shouldDisableDate={(date) =>
              date.getDate() === new Date().getDate()
            }
            slotProps={{
              textField: {
                error: Boolean(errorObj?.date) && Boolean(touchedObj?.date),
                id: 'Date',
                label: 'Start Date',
                name: 'date',
              },
            }}
          />
          <FormErrorHelperText
            error={
              Boolean(touchedObj?.date) && Boolean(errorObj?.date)
                ? errorObj?.date
                : undefined
            }
          />
        </FormControl>
        <FormControl fullWidth error={Boolean(errorObj?.projectId)}>
          <InputLabel
            id="fiterbyfieldlabel"
            error={
              Boolean(errorObj?.projectId) && Boolean(touchedObj?.projectId)
            }
          >
            Project
          </InputLabel>
          <Select
            label="Project"
            labelId="filterValuelabel"
            name={headcountChange.id ? 'tempProjectId' : 'projectId'}
            error={
              Boolean(errorObj?.projectId) && Boolean(touchedObj?.projectId)
            }
            value={headcountChange.tempProjectId || headcountChange.projectId}
            onChange={(e) =>
              handleDataChange(
                e.target.name,
                e.target.value,
                headcountChangeIndex,
              )
            }
            disabled={isSingleProjectSelected || headcountChange.delete}
          >
            {!projects?.length && (
              <MenuItem disabled>No options available.</MenuItem>
            )}

            {projects?.length &&
              projects.map((project: ProjectDetail) => (
                <MenuItem key={project.id} value={project.id}>
                  {project.name}
                </MenuItem>
              ))}
          </Select>
          <FormErrorHelperText
            error={
              Boolean(touchedObj?.projectId) && Boolean(errorObj?.projectId)
                ? errorObj?.projectId
                : undefined
            }
          />
        </FormControl>

        <FormControl sx={{ width: '40%' }}>
          <TextField
            id="countChange"
            type="number"
            sx={{
              '& .MuiInputBase-input': {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              },
            }}
            label="People"
            placeholder="Add headcount change"
            value={headcountChange.countChange}
            name={`countChange`}
            disabled={headcountChange.delete}
            error={
              Boolean(errorObj?.countChange) && Boolean(touchedObj?.countChange)
            }
            onChange={(e) =>
              handleDataChange(
                e.target.name,
                e.target.value,
                headcountChangeIndex,
              )
            }
          />
          <FormErrorHelperText
            error={
              Boolean(touchedObj?.countChange) && Boolean(errorObj?.countChange)
                ? errorObj?.countChange
                : undefined
            }
          />
        </FormControl>

        <FormControl sx={{ width: '60%' }}>
          <TextField
            id="blendedRate"
            type="number"
            label="Blended Rate"
            placeholder="Add blended rate"
            value={headcountChange.blendedRate}
            name={`blendedRate`}
            disabled={headcountChange.delete}
            error={
              Boolean(errorObj?.blendedRate) && Boolean(touchedObj?.blendedRate)
            }
            onChange={(e) =>
              handleDataChange(
                e.target.name,
                e.target.value,
                headcountChangeIndex,
              )
            }
          />
          <FormErrorHelperText
            error={
              Boolean(touchedObj?.blendedRate) && Boolean(errorObj?.blendedRate)
                ? errorObj?.blendedRate
                : undefined
            }
          />
        </FormControl>

        <FormControl sx={{ width: '60%' }}>
          <TextField
            id="blendedPayRate"
            type="number"
            label="Blended Pay Rate"
            placeholder="Add blended pay rate"
            value={headcountChange.blendedPayRate}
            name={`blendedPayRate`}
            disabled={headcountChange.delete}
            error={
              Boolean(errorObj?.blendedPayRate) &&
              Boolean(touchedObj?.blendedPayRate)
            }
            onChange={(e) =>
              handleDataChange(
                e.target.name,
                e.target.value,
                headcountChangeIndex,
              )
            }
          />
          <FormErrorHelperText
            error={
              Boolean(touchedObj?.blendedPayRate) &&
              Boolean(errorObj?.blendedPayRate)
                ? errorObj?.blendedPayRate
                : undefined
            }
          />
        </FormControl>

        <IconButton
          aria-label="Delete icon"
          onClick={() =>
            handleDeleteHeadcount(headcountChangeIndex, headcountChange.id)
          }
          disabled={headcountChange.delete}
          sx={{
            fontWeight: '400',
            fontSize: '14px',
            marginTop: '10px',
            width: 30,
            height: 30,
            alignSelf: 'top',
          }}
        >
          <DeleteOutlineOutlinedIcon
            sx={{ ...(headcountChange.delete && { color: 'lightgray' }) }}
          />
        </IconButton>
      </Box>
    </>
  );
};

export default HeadcountChangeGroup;
